import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { ROUTE } from '@/routes/constants';
import { ABOUT, BANNERS, IMG_BASE, IMG_V2, type Lang, type Pair } from './about-us-v2.data';
import './about-us-v2.css';

// 品牌 logo:繁體用 logo.svg(中文字样)、英文用 logo_en.svg(EASE);橙图标+黑字适配白顶栏
import LogoTc from '@/assets/images/logo.svg';
import LogoEn from '@/assets/images/logo_en.svg';

export default function AboutUsV2Page() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const current = Cookies.get('i18next') || i18n.language || 'zh-HK';
  const lang: Lang = current.startsWith('en') ? 'e' : 't';
  const tr = (p: Pair) => (lang === 'e' ? p.e : p.t);
  // changeLanguage 不写 cookie(LanguageDetector 已停用),需手动同步,否则旧 cookie 覆盖切换。
  const setLang = (lng: 'en' | 'zh-HK') => { i18n.changeLanguage(lng); Cookies.set('i18next', lng); };

  // 品牌词:把标题里的「易學平台 / EASE」高亮成橙色
  const BRAND = lang === 'e' ? 'EASE' : '易學平台';
  const hb = (s: string) =>
    s.split(BRAND).flatMap((seg, i) =>
      i === 0 ? [seg] : [<span key={i} className="bn-brand">{BRAND}</span>, seg],
    );
  // 角色卡标题:品牌橙 + 角色词(角色词颜色由父级 in-stu/in-tea 决定)
  const roleName = (name: Pair) => (
    <>
      <span className="bn-brand">{BRAND}</span>
      <span className="bn-role">{tr(name).split(BRAND)[1] ?? ''}</span>
    </>
  );

  const [navOpen, setNavOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<Set<number>>(new Set());
  const [popupOpen, setPopupOpen] = useState(false);

  // 锚点滚动:顶栏偏移由各区块 scroll-margin-top 声明式处理(见 about-us.css),故此处只需 scrollIntoView。
  const toSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView();
    setNavOpen(false);
  };

  const toggleFaq = (i: number) =>
    setOpenFaq((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div className={`about-v2${lang === 'e' ? ' lang-en' : ''}`}>
      {/* 顶栏：ease logo（点击回登录）+ 语言切换 + 小屏汉堡 */}
      <div className="top">
        <div className="topbar">
          <img className="log" src={lang === 'e' ? LogoEn : LogoTc} alt="易學平台" onClick={() => navigate(ROUTE.LOGIN)} />
          <div className="space" />
          <ul className="top_dh">
            <li className={lang === 't' ? 'active' : ''}>
              <div onClick={() => setLang('zh-HK')}>繁體</div>
            </li>
            <li className={lang === 'e' ? 'active' : ''}>
              <div onClick={() => setLang('en')}>EN</div>
            </li>
          </ul>
          <div id="toggleNav" onClick={() => setNavOpen((v) => !v)}>☰</div>
        </div>
      </div>

      <div className="content">
        {/* 右侧固定锚点导航 */}
        <ul className={`nav${navOpen ? ' open' : ''}`}>
          {ABOUT.nav.map((n) => (
            <li key={n.id} onClick={() => toSection(n.id)}>{tr(n.label)}</li>
          ))}
        </ul>

        {/* 系統簡介 — CSS banner */}
        <section id="introducei" className="bn-intro">
          {/* 1. Hero:整幅装饰背景图作 block <img> 等比缩放;标题/介绍绝对叠加,居中于左 76% 区(为右侧橙球留白) */}
          <div className="in-hero">
            <img className="in-hero-bg" src={`${IMG_V2}/intro-hero-bg.png`} alt="" />
            <div className="in-hero-text">
              <h2 className="in-h1">{hb(tr(BANNERS.intro.title))}</h2>
              <p className="in-lead">{tr(BANNERS.intro.lead)}</p>
            </div>
          </div>

          {/* 2. 系統包括：pale 底 + Student / Teacher 两行 */}
          <div className="in-system">
            <h3 className="in-sys-title">{hb(tr(BANNERS.intro.systemTitle))}</h3>

            <div className="in-role">
              <img className="in-role-img" src={`${IMG_V2}/${BANNERS.intro.student.img}.png`} alt="" />
              <div className="in-role-body in-stu">
                <h4 className="in-role-name">{roleName(BANNERS.intro.student.name)}</h4>
                <div className="in-role-sub">{tr(BANNERS.intro.student.subtitle)}</div>
                <ul className="in-points">
                  {BANNERS.intro.student.points.map((p, i) => <li key={i}>{tr(p)}</li>)}
                </ul>
              </div>
            </div>

            <div className="in-role in-role-rev">
              <img className="in-role-img" src={`${IMG_V2}/${BANNERS.intro.teacher.img}.png`} alt="" />
              <div className="in-role-body in-tea">
                <h4 className="in-role-name">{roleName(BANNERS.intro.teacher.name)}</h4>
                <div className="in-role-sub">{tr(BANNERS.intro.teacher.subtitle)}</div>
                <ul className="in-points">
                  {BANNERS.intro.teacher.points.map((p, i) => <li key={i}>{tr(p)}</li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* 3. 為什麼選擇：mint 底 + 白条标题 + 4 个特色行 */}
          <div className="in-why">
            <div className="in-why-bar"><h3>{hb(tr(BANNERS.intro.whyTitle))}</h3></div>
            {BANNERS.intro.features.map((f, i) => (
              <div className={`in-feat${i % 2 === 0 ? ' in-feat-rev' : ''}`} key={i}>
                <img className="in-feat-img" src={`${IMG_V2}/${f.img}.png`} alt="" />
                <div className="in-feat-card">
                  <h4 className="in-feat-title">{tr(f.title)}</h4>
                  <p className="in-feat-desc">{tr(f.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
        {/* 平台用戶 — CSS banner */}
        <section id="introduceu" className="bn-users">
          {/* 白条标题 1：白盒非通栏，左右/上方留橙色区块底（对照原图） */}
          <div className="us-bar us-bar-orange"><h3>{hb(tr(BANNERS.users.whoTitle))}</h3></div>

          {/* 橙色区：左半圆 + 数据 + 说明 + 资助机构 */}
          <div className="us-orange">
            <span className="us-semi" />
            <div className="us-orange-inner">
              <p className="us-stat">
                {tr(BANNERS.users.stat.prefix)} <span className="us-num">{BANNERS.users.stat.n1}</span> {tr(BANNERS.users.stat.mid)} <span className="us-num">{BANNERS.users.stat.n2}</span> {tr(BANNERS.users.stat.suffix)}
              </p>
              <p className="us-desc">{tr(BANNERS.users.desc)}</p>
              <div className="us-funds">
                <span className="us-fund-label">{tr(BANNERS.users.fundLabel)}</span>
                {BANNERS.users.funds.map((f) => (
                  <img key={f} className="us-fund" src={`${IMG_V2}/${f}.png`} alt="" />
                ))}
              </div>
            </div>
          </div>

          {/* 白条标题 2：白盒非通栏，左右/上方留绿色区块底（对照原图） */}
          <div className="us-bar us-bar-green"><h3>{hb(tr(BANNERS.users.reviewTitle))}</h3></div>

          {/* 评价区：左气泡 + 中央手机 + 右气泡 */}
          <div className="us-reviews">
            <div className="us-col">
              {[0, 2].map((i) => {
                const r = BANNERS.users.testimonials[i];
                return (
                  <div className="us-bubble" key={i}>
                    <p className="us-quote">{tr(r.quote)}</p>
                    <p className="us-author">— {tr(r.author)}</p>
                  </div>
                );
              })}
            </div>
            <img className="us-phone" src={`${IMG_V2}/${BANNERS.users.phoneImg}.png`} alt="" />
            <div className="us-col">
              {[1, 3].map((i) => {
                const r = BANNERS.users.testimonials[i];
                return (
                  <div className="us-bubble" key={i}>
                    <p className="us-quote">{tr(r.quote)}</p>
                    <p className="us-author">— {tr(r.author)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 帳戶申請 */}
        <div className="apply" id="apply">
          <div className="h0">{tr(ABOUT.apply.title)}</div>
          <div className="row">
            <img className="applybg" src={`${IMG_BASE}/applybg.png`} alt="" />
            <div className="applytip">
              <div className="t1">{tr(ABOUT.apply.t1)}</div>
              <div className="t2" dangerouslySetInnerHTML={{ __html: tr(ABOUT.apply.t2) }} />
              <div className="applybtn" onClick={() => setPopupOpen(true)}>{tr(ABOUT.apply.button)}</div>
            </div>
          </div>
        </div>

        {/* 常見問題 */}
        <div className="faq" id="faq">
          <div className="h0">{tr(ABOUT.faqTitle)}</div>
          {ABOUT.faq.map((item, i) => {
            const open = openFaq.has(i);
            return (
              <div className="item" key={i}>
                <div className="q" onClick={() => toggleFaq(i)}>
                  <span>{tr(item.q)}</span>
                  <span className="toggleico">
                    <img src={`${IMG_BASE}/${open ? 'collapse0' : 'expand0'}.png`} alt="" />
                  </span>
                </div>
                {open && (
                  <div className="a">
                    {item.a.map((para, j) => (
                      <div key={j}>
                        {item.a.length > 1 && <span className="dot">●</span>}
                        <span>{tr(para)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 研究成果 */}
        <div className="linkwrap" id="link">
          <div className="achtitlewrap">
            <div className="achtitle">{tr(ABOUT.publicationsTitle)}</div>
          </div>
          <ul className="link">
            {ABOUT.publications.map((p, i) => (
              <li key={i}>
                <img src={`${IMG_BASE}/${p.img}.png`} alt="" />
                <div className="text">
                  {p.title}<br />
                  {p.authors}<br />
                  {p.journal}
                  {p.link && (
                    <>
                      <br />
                      <span className="l">Links: </span>
                      <a target="_blank" rel="noreferrer" href={p.link}>{p.link}</a>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* 團隊成員 */}
        <div className="member" id="member">
          <div className="title">
            <span>{tr(ABOUT.membersTitle.brand)}</span>
            <span className="red">{tr(ABOUT.membersTitle.suffix)}</span>
          </div>
          <ul className="members">
            {ABOUT.members.map((m, i) => (
              <li key={i}>
                <div className="avatarwrap" style={m.bg ? { background: m.bg } : undefined}>
                  <img src={`${IMG_BASE}/photo/${m.photo}.png`} alt={tr(m.name)} />
                </div>
                <div className="b">{tr(m.name)}</div>
                <div className="t">
                  <div>{tr(ABOUT.orgLabels[m.org])}</div>
                  <div>{tr(ABOUT.orgLabels.lab)}</div>
                  <div>{tr(m.role)}</div>
                </div>
              </li>
            ))}
            {/* 占位 li：复刻原页末尾空格补齐网格 */}
            <li />
          </ul>
        </div>

        {/* 聯絡我們 — CSS banner */}
        <section id="introduceb" className="bn-contact">
          <img className="ct-wave" src={`${IMG_V2}/contact-wave.png`} alt="" />
          <img className="ct-people" src={`${IMG_V2}/${BANNERS.contact.img}.png`} alt="" />
          <div className="ct-content">
            <h2 className="ct-title">{tr(BANNERS.contact.title)}</h2>
            <div className="ct-row">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="#665350" aria-hidden="true"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z"/></svg>
              <span>{tr(BANNERS.contact.address)}</span>
            </div>
            <div className="ct-row">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#665350" strokeWidth="2" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
              <span>{BANNERS.contact.email}</span>
            </div>
            <div className="ct-row">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="#665350" aria-hidden="true"><path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2z" /></svg>
              <span>{BANNERS.contact.phone}</span>
            </div>
          </div>
        </section>
      </div>

      {/* 申请表弹窗：完整 UI，提交禁用、不发请求 */}
      {popupOpen && (
        <>
          <div className="popup-mask" onClick={() => setPopupOpen(false)} />
          <div className="popup">
            <img className="close" src={`${IMG_BASE}/close.svg`} alt="close" onClick={() => setPopupOpen(false)} />
            <div className="title">{tr(ABOUT.form.title)}</div>
            <div className="items">
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.firstname)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.lastname)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.email)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.phone)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.depart)}</div><input type="text" /></div>
              <div className="item">
                <div className="h3">{tr(ABOUT.form.fields.departtype)}</div>
                <select defaultValue="">
                  {ABOUT.form.options.map((o, i) => (
                    <option key={i} value={i === 0 ? '' : i - 1}>{tr(o)}</option>
                  ))}
                </select>
              </div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.subject)}</div><input type="text" /></div>
              <div className="item"><div className="h3">{tr(ABOUT.form.fields.grade)}</div><input type="text" /></div>
              <div className="item fullitem"><div className="h3">{tr(ABOUT.form.fields.remark)}</div><textarea rows={4} /></div>
              <div className="tip">{tr(ABOUT.form.tip)}</div>
              <div className="btnbar">
                <button className="btn" type="button" disabled>{tr(ABOUT.form.submit)}</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
